<template>
    <div>
        <p v-if="loadError" class="error" data-testid="onboarding-requests-error">
            Loading onboarding requests failed.
        </p>
        <DataTable
            v-else
            :data="requests"
            :columns="requestColumns"
            :loading="isLoading"
            enable-search
            enable-pagination
            enable-sorting
            @row-click="handleRowClick"
        />
    </div>
</template>

<script setup lang="ts">
import { h, onMounted, ref } from 'vue';
import type { ColumnDef } from '@tanstack/vue-table';

import DataTable from '@/components/tables/DataTable.vue';
import TagComponent from '@/components/ui/tag/TagComponent.vue';
import type { OnboardingRequestRow } from '@/gen/types/OnboardingRequestRow';
import { listAllRequests } from '@/views/admin/organization/org-wizard/org-wizard-api';
import { onboardingStageDisplay } from '@/views/admin/organization/org-wizard/onboarding-stage';
import { formatDate } from '@/stores/helpers';
import { useOrganizationStore } from '@/stores/organization';

const emit = defineEmits<{
    open: [draftId: string];
    loaded: [count: number];
}>();

const requests = ref<OnboardingRequestRow[]>([]);
const isLoading = ref(true);
const loadError = ref(false);

onMounted(async () => {
    try {
        // Wait for the org list so the X-Organization-Id interceptor can resolve.
        await useOrganizationStore().startupComplete;
        const response = await listAllRequests();
        requests.value = response.requests;
        emit('loaded', Number(response.totalCount));
    } catch {
        loadError.value = true;
    } finally {
        isLoading.value = false;
    }
});

function handleRowClick(request: OnboardingRequestRow) {
    emit('open', request.id);
}

const requestColumns: ColumnDef<OnboardingRequestRow, unknown>[] = [
    {
        accessorKey: 'organizationName',
        header: 'Organization',
        cell: (info) => (info.getValue() as string) || 'Unnamed draft',
        enableSorting: true,
    },
    {
        accessorKey: 'initiatedByDisplayName',
        header: 'Initiated by',
        // The name is snapshotted at draft creation (ITDEVELOP-3227); drafts older than that
        // column carry null, and the shortened user id is the only handle left for them.
        cell: (info) => (info.getValue() as string | null) || info.row.original.initiatedByUserId.slice(0, 8),
        enableSorting: true,
    },
    {
        accessorKey: 'internalNotes',
        header: 'Internal notes',
        cell: (info) => {
            const value = (info.getValue() as string | null) ?? '';
            if (!value) return '';
            // Truncate in the cell; the full text reveals on hover via the title.
            return h('span', { class: 'block max-w-64 truncate', title: value }, value);
        },
        enableSorting: false,
    },
    {
        accessorKey: 'stage',
        header: 'Status',
        cell: (info) => {
            const display = onboardingStageDisplay(info.getValue() as OnboardingRequestRow['stage']);
            return h(TagComponent, { value: display.label, variant: display.variant, showDot: true });
        },
        enableSorting: true,
    },
    {
        accessorKey: 'updatedAtUtc',
        header: 'Updated',
        cell: (info) => formatDate(info.getValue() as string),
        enableSorting: true,
    },
];
</script>
